import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Content } from '../data/content';
import { request402, settle, type PaymentOption } from './x402';

/**
 * The reader's support state — credits balance, a (mock) connected wallet, the
 * running "creators backed / spent" tally, and which content they've unlocked.
 *
 * Persists unlocked ids + tallies to localStorage so backing a creator sticks
 * across reloads. Stands in for a real account + the MemeGenes content-access
 * entitlement that a signed-in reader would carry server-side.
 */

const STORAGE_KEY = 'provny.unlocks';

// Prototype starting balance. 1 credit = 1¢ (see x402.ts).
const STARTING_CREDITS = 100;

type Persisted = {
  unlockedIds: string[];
  supportedCreators: string[]; // distinct handles backed
  spentCents: number;
  credits: number;
};

const fresh = (): Persisted => ({
  unlockedIds: [],
  supportedCreators: [],
  spentCents: 0,
  credits: STARTING_CREDITS,
});

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const finite = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

// SECURITY: this localStorage state is a UX cache only — fully client-controlled
// and trivially tampered. When the real MemeGenes content-access backend lands,
// the server is the source of truth for entitlements AND credit balance; the
// 402 settlement proof gates access there, never this flag. Coerce on read so a
// corrupt/tampered blob can't poison tallies (NaN credits, non-string ids).
function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return {
        unlockedIds: strings(p.unlockedIds),
        supportedCreators: strings(p.supportedCreators),
        spentCents: finite(p.spentCents, 0),
        credits: finite(p.credits, STARTING_CREDITS),
      };
    }
  } catch {
    // ignore corrupt storage — fall through to defaults
  }
  return fresh();
}

type WalletContextValue = {
  credits: number;
  walletConnected: boolean;
  /** Count of distinct creators backed — drives the Header support tally. */
  supportedCreators: number;
  spentCents: number;
  isUnlocked: (id: string) => boolean;
  unlock: (content: Content, option: PaymentOption) => Promise<void>;
  connectWallet: () => void;
  /** The 402 options for a piece of content — the two ways to back it. */
  optionsFor: (content: Content) => PaymentOption[];
};

const WalletContext = createContext<WalletContextValue | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Persisted>(load);
  const [walletConnected, setWalletConnected] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const unlockedSet = useMemo(() => new Set(state.unlockedIds), [state.unlockedIds]);

  const isUnlocked = useCallback((id: string) => unlockedSet.has(id), [unlockedSet]);

  const optionsFor = useCallback(
    (content: Content) => request402(content.id, content.priceCents).accepts,
    [],
  );

  const unlock = useCallback(async (content: Content, option: PaymentOption) => {
    // MOCK settlement — a real backend would charge + return a verified proof.
    await settle(option);

    // The functional updater is the single source of truth for the guard and
    // every tally — safe against double-unlock and concurrent settles.
    setState((prev) => {
      if (prev.unlockedIds.includes(content.id)) return prev;
      const supported = prev.supportedCreators.includes(content.creator)
        ? prev.supportedCreators
        : [...prev.supportedCreators, content.creator];
      // Credits draw down the balance; wallet pays "off-account".
      const credits =
        option.scheme === 'credits' ? Math.max(0, prev.credits - content.priceCents) : prev.credits;
      return {
        unlockedIds: [...prev.unlockedIds, content.id],
        supportedCreators: supported,
        spentCents: prev.spentCents + content.priceCents,
        credits,
      };
    });
  }, []);

  const connectWallet = useCallback(() => setWalletConnected((c) => !c), []);

  const value = useMemo<WalletContextValue>(
    () => ({
      credits: state.credits,
      walletConnected,
      supportedCreators: state.supportedCreators.length,
      spentCents: state.spentCents,
      isUnlocked,
      unlock,
      connectWallet,
      optionsFor,
    }),
    [state, walletConnected, isUnlocked, unlock, connectWallet, optionsFor],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
}
